import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, CreditCard, Loader2 } from "lucide-react";

export default function Credits() {
  const [abonnements, setAbonnements] = useState([]);
  const [credits, setCredits] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAb, setOpenAb] = useState(false);
  const [openCr, setOpenCr] = useState(false);
  const [abForm, setAbForm] = useState({ entreprise_id: "", type: "annuel", montant: 0, annee: new Date().getFullYear(), credits_inclus: 0 });
  const [crForm, setCrForm] = useState({ entreprise_id: "", nb_credits: 0, motif: "" });

  const load = async () => {
    setLoading(true);
    const [a, c, e] = await Promise.all([api.get("/abonnements"), api.get("/credits"), api.get("/entreprises")]);
    setAbonnements(a.data); setCredits(c.data); setEntreprises(e.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submitAb = async (e) => {
    e.preventDefault();
    try {
      await api.post("/abonnements", { ...abForm, montant: Number(abForm.montant), annee: Number(abForm.annee), credits_inclus: Number(abForm.credits_inclus) });
      toast.success("Abonnement créé"); setOpenAb(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitCr = async (e) => {
    e.preventDefault();
    try {
      await api.post("/credits", { ...crForm, nb_credits: Number(crForm.nb_credits) });
      toast.success("Crédits ajoutés"); setOpenCr(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const entLabel = (id) => entreprises.find(e => e.id === id)?.raison_sociale || "—";
  const soldeParEntreprise = entreprises.map(e => ({
    ...e, solde: credits.filter(c => c.entreprise_id === e.id).reduce((s, c) => s + Number(c.nb_credits), 0),
  }));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>;

  return (
    <div data-testid="credits-page">
      <PageHeader title="Abonnements & Crédits" subtitle="Abonnements annuels et crédits de services par entreprise membre." />

      <Tabs defaultValue="soldes" className="mt-6">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="soldes" data-testid="tab-soldes">Soldes par entreprise</TabsTrigger>
          <TabsTrigger value="abonnements" data-testid="tab-abonnements">Abonnements</TabsTrigger>
          <TabsTrigger value="mouvements" data-testid="tab-mouvements">Mouvements de crédits</TabsTrigger>
        </TabsList>

        <TabsContent value="soldes" className="mt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {soldeParEntreprise.map(e => (
              <div key={e.id} className="kpi-card bg-white border border-slate-200 rounded-xl p-5">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{e.territoire || "—"}</div>
                <h3 className="mt-2 text-base font-semibold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{e.raison_sociale}</h3>
                <div className="mt-4 flex items-end gap-2">
                  <span className="text-3xl font-semibold text-[#C5A059]" style={{ fontFamily: "Outfit, sans-serif" }}>{e.solde}</span>
                  <span className="text-sm text-slate-500 mb-1">crédits</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="abonnements" className="mt-5">
          <div className="flex justify-end mb-4">
            <Dialog open={openAb} onOpenChange={setOpenAb}>
              <DialogTrigger asChild>
                <Button data-testid="ab-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                  <Plus className="h-4 w-4 mr-2" /> Nouvel abonnement
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nouvel abonnement annuel</DialogTitle></DialogHeader>
                <form onSubmit={submitAb} className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Entreprise *</Label>
                    <Select value={abForm.entreprise_id} onValueChange={(v)=>setAbForm({...abForm, entreprise_id: v})}>
                      <SelectTrigger data-testid="ab-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Année *</Label><Input type="number" required value={abForm.annee} onChange={(e)=>setAbForm({...abForm, annee: e.target.value})} /></div>
                  <div className="space-y-2"><Label>Montant (€) *</Label><Input type="number" step="0.01" required value={abForm.montant} onChange={(e)=>setAbForm({...abForm, montant: e.target.value})} /></div>
                  <div className="space-y-2 col-span-2"><Label>Crédits inclus</Label><Input type="number" value={abForm.credits_inclus} onChange={(e)=>setAbForm({...abForm, credits_inclus: e.target.value})} /></div>
                  <DialogFooter className="col-span-2"><Button type="submit" data-testid="ab-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Année</th><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Montant</th><th className="px-4 py-3 text-right">Crédits</th><th className="px-4 py-3">Statut</th></tr></thead>
              <tbody>{abonnements.map(a => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{a.annee}</td>
                  <td className="px-4 py-3 text-slate-700">{entLabel(a.entreprise_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{a.type}</td>
                  <td className="px-4 py-3 text-right font-medium">{Number(a.montant).toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-3 text-right text-slate-700">{a.credits_inclus}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700">{a.statut}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="mouvements" className="mt-5">
          <div className="flex justify-end mb-4">
            <Dialog open={openCr} onOpenChange={setOpenCr}>
              <DialogTrigger asChild>
                <Button data-testid="cr-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                  <Plus className="h-4 w-4 mr-2" /> Mouvement crédits
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Mouvement de crédits</DialogTitle></DialogHeader>
                <form onSubmit={submitCr} className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Entreprise *</Label>
                    <Select value={crForm.entreprise_id} onValueChange={(v)=>setCrForm({...crForm, entreprise_id: v})}>
                      <SelectTrigger data-testid="cr-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Nb crédits (+ ou −)</Label><Input type="number" required value={crForm.nb_credits} onChange={(e)=>setCrForm({...crForm, nb_credits: e.target.value})} /></div>
                  <div className="space-y-2"><Label>Motif</Label><Input value={crForm.motif} onChange={(e)=>setCrForm({...crForm, motif: e.target.value})} /></div>
                  <DialogFooter><Button type="submit" data-testid="cr-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {credits.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
              <CreditCard className="h-10 w-10 mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500">Aucun mouvement.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3 text-right">Crédits</th><th className="px-4 py-3">Motif</th></tr></thead>
                <tbody>{credits.map(c => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600 text-xs">{c.created_at?.slice(0,10)}</td>
                    <td className="px-4 py-3 text-slate-700">{entLabel(c.entreprise_id)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${Number(c.nb_credits) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{c.nb_credits > 0 ? "+" : ""}{c.nb_credits}</td>
                    <td className="px-4 py-3 text-slate-600">{c.motif || "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
